import { useEffect, useMemo, useState } from "react";
import { fnbAPI, fmtINR } from "./fnbApi";
import { toast } from "sonner";
import { Plus, X, Search, Pencil, Wine, Utensils } from "lucide-react";
import { FormField, errorRing } from "../../components/form/FormField";
import { useFormValidation } from "../../hooks/useFormValidation";
import { required, minNumber } from "../../lib/validators";
import { NumberInput } from "@/components/ui/number-input";

export default function FnbMenu() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [addCatOpen, setAddCatOpen] = useState(false);

  const load = () => Promise.all([
    fnbAPI.categories(), fnbAPI.menuItems(), fnbAPI.outlets(),
  ]).then(([c, i, o]) => { setCats(c); setItems(i); setOutlets(o); });
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = items;
    if (activeCat) arr = arr.filter(i => i.category_id === activeCat);
    if (q) arr = arr.filter(i => i.name.toLowerCase().includes(q.toLowerCase()));
    return arr;
  }, [items, activeCat, q]);

  const toggleAvail = async (item) => {
    try {
      await fnbAPI.updateMenuItem(item.id, { is_available: !item.is_available });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="fnb-menu-page">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search menu…" aria-label="Search menu items"
            data-testid="fnb-menu-search"
            className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
        </div>
        <button onClick={() => setAddCatOpen(true)} data-testid="fnb-menu-add-cat"
          className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-[12px] font-medium inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5"/>Category</button>
        <button onClick={() => setShowAdd(true)} data-testid="fnb-menu-add-btn"
          className="px-3 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[12px] font-medium inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5"/>Menu Item</button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setActiveCat(null)}
          className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${!activeCat ? "bg-hocp-mint text-hocp-deepTeal" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>All</button>
        {cats.map(c => (
          <button key={c.id} onClick={() => setActiveCat(c.id)}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${activeCat === c.id ? "bg-hocp-mint text-hocp-deepTeal" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{c.name}</button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-slate-400" data-testid="fnb-menu-empty">No items match.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Category</th>
                <th className="text-left px-4 py-2.5">Outlet</th>
                <th className="text-right px-4 py-2.5">Price</th>
                <th className="text-right px-4 py-2.5">Tax %</th>
                <th className="text-right px-4 py-2.5">Prep</th>
                <th className="text-left px-4 py-2.5">Recipe</th>
                <th className="text-left px-4 py-2.5">Available</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(m => {
                const cat = cats.find(c => c.id === m.category_id)?.name;
                const outlet = outlets.find(o => o.id === m.production_outlet_id);
                return (
                  <tr key={m.id} className="hover:bg-slate-50" data-testid={`fnb-menu-row-${m.id}`}>
                    <td className="px-4 py-2.5 font-medium text-slate-800">
                      {m.name} {m.is_alcoholic && <span className="text-[10px] px-1.5 py-0.5 ml-1 rounded bg-fuchsia-100 text-fuchsia-700 uppercase">21+</span>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{cat}</td>
                    <td className="px-4 py-2.5 text-slate-600">
                      <span className="inline-flex items-center gap-1">
                        {outlet?.kind === "bar" ? <Wine className="w-3 h-3"/> : <Utensils className="w-3 h-3"/>}
                        {outlet?.name}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums">{fmtINR(m.price)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-500 tabular-nums">{m.tax_pct}%</td>
                    <td className="px-4 py-2.5 text-right text-slate-500 tabular-nums">{m.prep_time_min || "—"}m</td>
                    <td className="px-4 py-2.5 text-slate-500 text-[11.5px]">
                      {(m.recipe || []).length ? `${m.recipe.length} ingredient(s) · v${m.recipe_version || 1}` : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <button onClick={() => toggleAvail(m)} data-testid={`fnb-menu-toggle-${m.id}`}
                        className={`text-[11px] px-2 py-0.5 rounded-md font-medium ${m.is_available ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {m.is_available ? "Available" : "Unavailable"}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => setEditing(m)} data-testid={`fnb-menu-edit-${m.id}`}
                        className="text-[12px] text-hocp-navy hover:underline inline-flex items-center gap-1"><Pencil className="w-3 h-3"/>Edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {addCatOpen && <CategoryDialog onClose={() => setAddCatOpen(false)} onSaved={() => { setAddCatOpen(false); load(); }}/>}
      {(showAdd || editing) && (
        <MenuItemDialog cats={cats} outlets={outlets} initial={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); load(); }}/>
      )}
    </div>
  );
}

function CategoryDialog({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [sort, setSort] = useState(100);
  const [busy, setBusy] = useState(false);
  const v = useFormValidation({ name: [required("Category name is required")], sort_order: [minNumber(0)] });
  const submit = async (e) => {
    e.preventDefault();
    if (!v.validateAll({ name, sort_order: sort })) return;
    setBusy(true);
    try { await fnbAPI.createCategory({ name, sort_order: sort }); toast.success("Category added"); onSaved(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fnb-cat-dialog">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">Add Category</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3" noValidate>
          <FormField label="Category name" htmlFor="fnb-cat-name" required error={v.fieldError("name")}>
            <input id="fnb-cat-name" value={name} onChange={e => setName(e.target.value)} onBlur={() => v.handleBlur("name", name)} placeholder="e.g. Starters"
              data-testid="fnb-cat-name" className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] ${errorRing(v.fieldError("name"))}`}/>
          </FormField>
          <FormField label="Sort order" htmlFor="fnb-cat-sort" optional error={v.fieldError("sort_order")} hint="Lower numbers appear first">
            <NumberInput id="fnb-cat-sort" value={sort} onChange={e => setSort(Number(e.target.value))} onBlur={() => v.handleBlur("sort_order", sort)} placeholder="e.g. 100"
              data-testid="fnb-cat-sort" className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] ${errorRing(v.fieldError("sort_order"))}`}/>
          </FormField>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[13px] text-slate-600">Cancel</button>
            <button type="submit" disabled={busy} data-testid="fnb-cat-submit"
              className="px-5 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-semibold">{busy ? "…" : "Add"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MenuItemDialog({ cats, outlets, initial, onClose, onSaved }) {
  const [f, setF] = useState({
    name: initial?.name || "", category_id: initial?.category_id || (cats[0]?.id || ""),
    production_outlet_id: initial?.production_outlet_id || (outlets[0]?.id || ""),
    price: initial?.price || "", tax_pct: initial?.tax_pct || "5",
    prep_time_min: initial?.prep_time_min || 10,
    is_alcoholic: initial?.is_alcoholic || false,
    is_available: initial?.is_available !== false,
    recipe: initial?.recipe || [],
  });
  const [busy, setBusy] = useState(false);
  const v = useFormValidation({
    name: [required("Item name is required")],
    price: [required("Price is required"), minNumber(0, "Price can't be negative")],
    tax_pct: [minNumber(0, "Tax can't be negative")],
    prep_time_min: [minNumber(0, "Prep time can't be negative")],
  });
  const addRecipeLine = () => setF({...f, recipe: [...f.recipe, { supply_item_id: "", supply_item_name: "", qty: "0", unit: "g" }]});
  const upd = (i, key, val) => setF({...f, recipe: f.recipe.map((r, ix) => ix === i ? { ...r, [key]: val } : r)});
  const rmRecipe = (i) => setF({...f, recipe: f.recipe.filter((_, ix) => ix !== i)});
  const submit = async (e) => {
    e.preventDefault();
    if (!v.validateAll(f)) return;
    setBusy(true);
    try {
      const body = { ...f, recipe: f.recipe.filter(r => r.supply_item_name) };
      if (initial) await fnbAPI.updateMenuItem(initial.id, body);
      else         await fnbAPI.createMenuItem(body);
      toast.success(initial ? "Menu item updated" : "Menu item added");
      onSaved();
    } catch (er) { toast.error(er?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fnb-menu-dialog">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">{initial ? "Edit Menu Item" : "Add Menu Item"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3" noValidate>
          <FormField label="Item name" htmlFor="fnb-mi-name" required error={v.fieldError("name")}>
            <input id="fnb-mi-name" value={f.name} onChange={e => setF({...f, name: e.target.value})} onBlur={() => v.handleBlur("name", f.name)} placeholder="e.g. Masala Dosa"
              data-testid="fnb-mi-name" className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] ${errorRing(v.fieldError("name"))}`}/>
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Category" htmlFor="fnb-mi-cat">
              <select id="fnb-mi-cat" value={f.category_id} onChange={e => setF({...f, category_id: e.target.value})} data-testid="fnb-mi-cat"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] bg-white">
                {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </FormField>
            <FormField label="Production outlet" htmlFor="fnb-mi-outlet">
              <select id="fnb-mi-outlet" value={f.production_outlet_id} onChange={e => setF({...f, production_outlet_id: e.target.value})} data-testid="fnb-mi-outlet"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] bg-white">
                {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </FormField>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <FormField label="Price (₹)" htmlFor="fnb-mi-price" required error={v.fieldError("price")}>
              <input id="fnb-mi-price" value={f.price} onChange={e => setF({...f, price: e.target.value})} onBlur={() => v.handleBlur("price", f.price)} placeholder="e.g. 250" data-testid="fnb-mi-price" className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono ${errorRing(v.fieldError("price"))}`}/>
            </FormField>
            <FormField label="Tax %" htmlFor="fnb-mi-tax" error={v.fieldError("tax_pct")}>
              <input id="fnb-mi-tax" value={f.tax_pct} onChange={e => setF({...f, tax_pct: e.target.value})} onBlur={() => v.handleBlur("tax_pct", f.tax_pct)} placeholder="e.g. 5" data-testid="fnb-mi-tax" className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono ${errorRing(v.fieldError("tax_pct"))}`}/>
            </FormField>
            <FormField label="Prep time (min)" htmlFor="fnb-mi-prep" error={v.fieldError("prep_time_min")}>
              <NumberInput id="fnb-mi-prep" value={f.prep_time_min} onChange={e => setF({...f, prep_time_min: Number(e.target.value)})} onBlur={() => v.handleBlur("prep_time_min", f.prep_time_min)} placeholder="e.g. 10" data-testid="fnb-mi-prep" className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono ${errorRing(v.fieldError("prep_time_min"))}`}/>
            </FormField>
          </div>
          <div className="flex gap-4">
            <label className="text-[13px] flex items-center gap-1.5"><input type="checkbox" checked={f.is_alcoholic} onChange={e => setF({...f, is_alcoholic: e.target.checked})} data-testid="fnb-mi-alc"/>Alcoholic</label>
            <label className="text-[13px] flex items-center gap-1.5"><input type="checkbox" checked={f.is_available} onChange={e => setF({...f, is_available: e.target.checked})} data-testid="fnb-mi-avail"/>Available</label>
          </div>
          {/* Recipe */}
          <div className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Recipe (versioned)</div>
              <button type="button" onClick={addRecipeLine} data-testid="fnb-mi-recipe-add"
                className="text-[11px] text-hocp-navy font-medium hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3"/>Add ingredient</button>
            </div>
            {f.recipe.length === 0 ? (
              <div className="text-[11px] text-slate-400 italic">No ingredients — inventory won&apos;t auto-decrement.</div>
            ) : (
              <>
                <div className="grid grid-cols-[1fr_60px_60px_20px] gap-1 mb-1 text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                  <span>Ingredient</span><span>Qty</span><span>Unit</span><span></span>
                </div>
                {f.recipe.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_60px_60px_20px] gap-1 mb-1">
                    <input value={r.supply_item_name} onChange={e => upd(i, "supply_item_name", e.target.value)} aria-label="Ingredient name" placeholder="e.g. Rice flour" className="px-2 py-1 border border-slate-200 rounded text-[12px]"/>
                    <input value={r.qty} onChange={e => upd(i, "qty", e.target.value)} aria-label="Quantity" placeholder="qty" className="px-2 py-1 border border-slate-200 rounded text-[12px] font-mono"/>
                    <input value={r.unit} onChange={e => upd(i, "unit", e.target.value)} aria-label="Unit" placeholder="unit" className="px-2 py-1 border border-slate-200 rounded text-[12px]"/>
                    <button type="button" onClick={() => rmRecipe(i)} className="text-rose-500"><X className="w-3 h-3"/></button>
                  </div>
                ))}
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[13px] text-slate-600">Cancel</button>
            <button type="submit" disabled={busy} data-testid="fnb-mi-submit"
              className="px-5 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-semibold">{busy ? "…" : (initial ? "Save" : "Add")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
